import { For, Show, createMemo } from "solid-js";
import type { Component } from "solid-js";
import { useShellStore } from "@ui/state/shell";
import { useRobotDetailQuery, useRobotRealtimeDetailQuery } from "../service";
import { CollapsibleSection } from "@ui/components/CollapsibleSection";

const labelValue = (label: string, value: string | null | undefined, valueClass?: string) => (
  <div class="robot-field">
    <span class="robot-field-label">{label.toUpperCase()}</span>
    <span class={`robot-field-value ${valueClass || ""}`}>{value ?? "-"}</span>
  </div>
);

const statusBadge = (status: string | null, isOnline?: boolean | null) => {
  if (!status) return "-";
  const badgeClass = isOnline === true ? "robot-status-badge online" :
                     isOnline === false ? "robot-status-badge offline" :
                     "robot-status-badge";
  return <span class={badgeClass}>{status}</span>;
};

const batteryIndicator = (level: number | null) => {
  if (level === null || Number.isNaN(level)) return "-";
  const percentage = Math.round(level);
  const batteryClass = percentage < 20 ? "robot-battery low" :
                      percentage < 50 ? "robot-battery medium" :
                      "robot-battery high";
  return (
    <div class={batteryClass}>
      <span class="robot-battery-level">{percentage}%</span>
      <div class="robot-battery-bar">
        <div class="robot-battery-fill" style={`width: ${percentage}%`}></div>
      </div>
    </div>
  );
};

const formatDateTime = (value: Date | null): string => (value ? value.toLocaleString() : "-");

const formatIsoDateTime = (value: string | null): string => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatBattery = (level: number | null): string => {
  if (level === null || Number.isNaN(level)) {
    return "-";
  }
  return `${Math.round(level)}%`;
};

const formatDuration = (seconds: number | null): string => {
  if (seconds === null || Number.isNaN(seconds)) {
    return "-";
  }
  const totalMinutes = Math.floor(seconds / 60);
  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hrs > 0) {
    return `${hrs}h ${mins}m`;
  }
  return `${mins}m`;
};

export const RobotDetailPanel: Component = () => {
  const { state } = useShellStore();
  const robotId = () => state.selectedRobotId;

  const detailQuery = useRobotDetailQuery(robotId);
  const realtimeQuery = useRobotRealtimeDetailQuery(robotId);

  const summaryDetail = () => detailQuery.data ?? null;
  const summaryRobot = () => summaryDetail()?.summary ?? null;
  const realtime = () => realtimeQuery.data ?? null;

  const provider = () => summaryRobot()?.provider ?? realtime()?.provider ?? "dreame";
  const manufacturer = () => summaryRobot()?.manufacturer ?? "Unknown";
  const deviceIdentifier = () =>
    summaryRobot()?.providerDeviceId ?? summaryRobot()?.id ?? realtime()?.deviceId ?? robotId() ?? "";

  const displayName = () => summaryRobot()?.displayName ?? realtime()?.displayName ?? summaryRobot()?.model ?? deviceIdentifier();
  const displayModel = () => summaryRobot()?.model ?? realtime()?.model ?? "Unknown";
  const displayStatus = () =>
    realtime()?.statusText ?? realtime()?.statusCode?.toString() ?? summaryRobot()?.status ?? null;
  const displayBattery = () => summaryRobot()?.batteryLevel ?? realtime()?.batteryLevel ?? null;
  const displayLocation = () =>
    summaryRobot()?.locationHint ?? summaryRobot()?.locationId ?? realtime()?.locationHint ?? null;
  const lastSeen = () => summaryRobot()?.lastSeenAt ?? null;
  const isOnline = () => summaryRobot()?.online ?? realtime()?.online ?? false;
  const isActive = () => summaryRobot()?.isActive ?? false;

  const telemetry = () => summaryDetail()?.telemetry ?? null;
  const telemetryEntries = createMemo(() => {
    const data = telemetry();
    if (!data) {
      return [] as Array<[string, string]>;
    }
    return Object.entries(data).map(([key, value]) => [key, typeof value === "string" ? value : JSON.stringify(value)]);
  });

  const realtimeProperties = createMemo(() => {
    const props = realtime()?.properties ?? [];
    return props.map((property) => ({
      key: property.label ?? `${property.siid ?? "?"}.${property.piid ?? "?"}`,
      value:
        typeof property.value === "string"
          ? property.value
          : property.value === null
          ? "-"
          : JSON.stringify(property.value),
    }));
  });

  const realtimeWarnings = () => realtime()?.warnings ?? [];
  const fetchedAt = () => realtime()?.fetchedAt ?? null;


  const isDetailLoading = () => Boolean(detailQuery.isLoading);
  const detailError = () =>
    detailQuery.error instanceof Error ? detailQuery.error : detailQuery.error ? new Error(String(detailQuery.error)) : null;

  const isRealtimeLoading = () => Boolean(realtimeQuery.isLoading);
  const realtimeError = () =>
    realtimeQuery.error instanceof Error
      ? realtimeQuery.error
      : realtimeQuery.error
      ? new Error(String(realtimeQuery.error))
      : null;


  const hasRealtimeData = () => Boolean(realtime());

  return (
    <div class="chat-profile-panel">
      <Show when={robotId()} fallback={<p>Select a robot to inspect its status.</p>}>
        <Show when={!isDetailLoading()} fallback={<p>Loading robot details...</p>}>
          <Show
            when={!detailError()}
            fallback={
              <p class="empty-state">
                Unable to load robot details right now.
                <Show when={detailError()?.message}>{(message) => <span class="error-detail">{message()}</span>}</Show>
              </p>
            }
          >
            <div class="robot-panel-header">
              <h2>{displayName()}</h2>
              <div class="robot-status-row">
                <Show when={isOnline()}>
                  <span class="robot-status-badge online">
                    <i class="fas fa-circle"></i> Online
                  </span>
                </Show>
                <Show when={!isOnline()}>
                  <span class="robot-status-badge offline">
                    <i class="fas fa-circle"></i> Offline
                  </span>
                </Show>
                <Show when={isActive()}>
                  <span class="robot-active-badge is-active">Active</span>
                </Show>
                <Show when={!isActive()}>
                  <span class="robot-active-badge is-inactive">Inactive</span>
                </Show>
                {batteryIndicator(displayBattery())}
              </div>
            </div>

            <div class="robot-info-section">
              <div class="robot-fields">
                {labelValue("Model", displayModel())}
                {labelValue("Manufacturer", manufacturer())}
                {labelValue("Provider", provider())}
                {labelValue("Device ID", deviceIdentifier(), "monospace")}
                {labelValue("Status", displayStatus())}
                {labelValue("Location", displayLocation())}
                {labelValue("Last seen", formatDateTime(lastSeen()))}
              </div>
              <Show when={!summaryRobot()}>
                <div class="robot-notice">
                  <i class="fas fa-info-circle"></i>
                  <span>Live data from provider - no stored record yet</span>
                </div>
              </Show>
            </div>

            <CollapsibleSection title="REAL-TIME STATUS" defaultOpen={true}>
              <Show when={!isRealtimeLoading()} fallback={<div class="robot-loading">Contacting robot...</div>}>
                <Show
                  when={!realtimeError()}
                  fallback={
                    <div class="robot-error">
                      <i class="fas fa-exclamation-triangle"></i>
                      <span>Unable to reach provider API</span>
                      <Show when={realtimeError()?.message}>{(message) => <div class="robot-error-detail">{message()}</div>}</Show>
                    </div>
                  }
                >
                  <Show when={hasRealtimeData()} fallback={<div class="robot-empty">No live data available</div>}>
                    {(data) => (
                      <div class="robot-realtime-content">
                        <div class="robot-fields">
                          {labelValue("Status", data().statusText ?? data().statusCode?.toString() ?? "-")}
                          {labelValue("Charging", data().chargingState === null ? "-" : String(data().chargingState))}
                          {labelValue("Location", data().locationHint ?? displayLocation())}
                          {labelValue("Updated", formatIsoDateTime(fetchedAt()))}
                        </div>
                        <Show when={realtimeWarnings().length > 0}>
                          <div class="robot-warnings">
                            <h4><i class="fas fa-exclamation-triangle"></i> Warnings</h4>
                            <ul>
                              <For each={realtimeWarnings()}>{(warning) => <li>{warning}</li>}</For>
                            </ul>
                          </div>
                        </Show>
                        <Show when={realtimeProperties().length > 0}>
                          <CollapsibleSection title="Device Properties" defaultOpen={false}>
                            <div class="robot-properties-grid">
                              <For each={realtimeProperties()}>
                                {(entry) => (
                                  <div class="robot-property">
                                    <div class="robot-property-key">{entry.key}</div>
                                    <div class="robot-property-value">{entry.value}</div>
                                  </div>
                                )}
                              </For>
                            </div>
                          </CollapsibleSection>
                        </Show>
                      </div>
                    )}
                  </Show>
                </Show>
              </Show>
            </CollapsibleSection>



            <CollapsibleSection title="SYSTEM INFO" defaultOpen={false}>
              <div class="robot-fields">
                {labelValue("Firmware", summaryDetail()?.firmwareVersion ?? null)}
                {labelValue("Assigned booking", summaryDetail()?.assignedBookingId ?? null)}
                {labelValue("Maintenance due", formatDateTime(summaryDetail()?.maintenanceDueAt ?? null))}
                {labelValue("Notes", summaryDetail()?.maintenanceNotes ?? null)}
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="TELEMETRY" defaultOpen={false}>
              <Show when={telemetryEntries().length > 0} fallback={<div class="robot-empty">No telemetry captured yet</div>}>
                <div class="robot-telemetry-grid">
                  <For each={telemetryEntries()}>
                    {([key, value]) => (
                      <div class="robot-telemetry-item">
                        <div class="robot-telemetry-key">{key}</div>
                        <div class="robot-telemetry-value">{value}</div>
                      </div>
                    )}
                  </For>
                </div>
              </Show>
            </CollapsibleSection>
          </Show>
        </Show>
      </Show>
    </div>
  );
};
